<script setup>
import { computed, ref } from 'vue';
import { useElementHeight } from '@/composables/use-element-height';
import { useInfiniteScroll } from '@/composables/use-infinite-scroll';
import { useRowFitCount } from '@/composables/use-row-fit-count';
import { SEARCH_SKELETON_COUNT } from '@/views/Search/constants';

const props = defineProps({
  items: { type: Array, required: true },
  component: { type: Object, default: null },
  itemProps: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  hasNextPage: { type: Boolean, default: false },
});

const emit = defineEmits(['load-more']);

const grid = ref(null);
const firstCard = ref(null);
const loadingAnchor = ref(null);

const columns = useRowFitCount(grid);
const cardHeight = useElementHeight(firstCard);

const skeletonCount = computed(() => {
  const rowSize = Number.isFinite(columns.value) ? columns.value : SEARCH_SKELETON_COUNT;
  const lastRowSize = props.items.length % rowSize;

  return lastRowSize ? rowSize - lastRowSize : rowSize;
});

const gridStyle = computed(() => (cardHeight.value ? { '--search-page-card-height': `${cardHeight.value}px` } : {}));

const setFirstCard = (card) => {
  firstCard.value = card?.$el ?? null;
};

useInfiniteScroll(loadingAnchor, {
  hasNextPage: () => props.hasNextPage,
  isFetchingNextPage: () => props.loading,
  fetchNextPage: () => emit('load-more'),
});
</script>

<template>
  <div class="search-page-results">
    <div ref="grid" class="search-page-results__grid" :style="gridStyle">
      <div v-for="(item, index) in items" :key="item.id" class="search-page-results__item">
        <component :is="component" :ref="index === 0 ? setFirstCard : undefined" :item="item" v-bind="itemProps" />
      </div>
      <template v-if="loading">
        <div v-for="index in skeletonCount" :key="`skeleton-${index}`" class="search-page-results__item">
          <span class="search-page-results__skeleton" />
        </div>
      </template>
    </div>
    <div ref="loadingAnchor" class="search-page-results__anchor" />
  </div>
</template>

<style lang="scss" scoped>
.search-page-results {
  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 40px 10px;

    @include mobile-sm-max {
      justify-content: center;
    }
  }

  &__item {
    flex-shrink: 0;
    width: 250px;
    height: 150px;
    padding: 5px;
  }

  &__skeleton {
    display: block;
    height: var(--search-page-card-height, 140px);
    border-radius: 10px;
    background: $black40;
    animation: search-page-results-pulse 1.4s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &__anchor {
    height: 1px;
  }
}

@keyframes search-page-results-pulse {
  50% {
    opacity: 0.5;
  }
}
</style>
